import React, {useState, useEffect, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import '../Popup.css';
import ZipCodes from "../Header/ZipCodes";
import api from '../../api';
import {useUser} from '../../context/UserContext';
import Cookies from 'js-cookie';
import EditProfilePopup from "../popups/EditProfilePopup";
import EditLanguagesPopup from "../popups/EditLanguagesPopup";
import EditServicesPopup from "../popups/EditServicesPopup";
import EditAwardsPopup from "../popups/EditAwardsPopup";
import EditWorkersPopup from "../popups/EditWorkersPopup";
import {useNotification} from "../../context/NotificationContext";
import Notification from "../Notification";
import Loader from '../Loader';

import CropImageModal from "../popups/CropImageModal";
import {validateAndScaleImage} from '../../utils/validateAndScaleImage';

import VendorBanner from './PopupVendor/VendorBanner';
import VendorProfileHeader from './PopupVendor/VendorProfileHeader';
import VendorDetails from './PopupVendor/VendorDetails';
import VendorServicesSection from './PopupVendor/VendorServicesSection';
import VendorLanguagesSection from './PopupVendor/VendorLanguagesSection';
import VendorAwardsSection from './PopupVendor/VendorAwardsSection';
import VendorWorkersSection from './PopupVendor/VendorWorkersSection';

import PopupVendorRequestForm from '../popups/PopupVendorRequestForm';
// --- ИМПОРТ НАШЕЙ НОВОЙ ПОЛНОФУНКЦИОНАЛЬНОЙ МОДАЛКИ ДЛЯ ВЕНДОРА ---
import ClaimVendorProfileModal from '../popups/ClaimVendorProfileModal';


const IMAGE_REQUIREMENTS = {
    'vendor_photo': {min_width: 456, min_height: 256, aspect_ratio: 1.78, ratio_tolerance: 0.05},
    'background_vendor': {min_width: 1440, min_height: 280, aspect_ratio: 5.14, ratio_tolerance: 0.05},
};

const stateAbbreviations = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA", "Colorado": "CO",
    "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
    "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
    "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
    "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
    "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA",
    "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX",
    "Utah": "UT", "Vermont": "VT", "Virginia": "VA", "Washington": "WA", "West Virginia": "WV",
    "Wisconsin": "WI", "Wyoming": "WY"
};

const lawLawUserIdFromEnv = process.env.REACT_APP_LAW_LAW_USER_ID;
const LAW_LAW_USER_ID = parseInt(lawLawUserIdFromEnv, 10);

const PopupVendor = ({visible, vendor: initialVendorProp, onClose, showSharePopup, origin}) => {
    const {user, isAuthenticated} = useUser();
    const location = useLocation();
    const navigate = useNavigate();

    const [currentVendor, setVendorState] = useState(initialVendorProp);
    const [currentLogo, setLogo] = useState(initialVendorProp?.logo);
    const [currentBackgroundImage, setBackgroundImage] = useState(initialVendorProp?.background_image);
    const [isFavorite, setIsFavorite] = useState(initialVendorProp?.is_favorite || false);
    const [loading, setLoading] = useState(false);

    const [showEditProfilePopup, setShowEditProfilePopup] = useState(false);
    const [showEditLanguagesPopup, setShowEditLanguagesPopup] = useState(false);
    const [showEditServicesPopup, setShowEditServicesPopup] = useState(false);
    const [showEditAwardsPopup, setShowEditAwardsPopup] = useState(false);
    const [showEditWorkersPopup, setShowEditWorkersPopup] = useState(false);

    const [expandedCities, setExpandedCities] = useState({});

    const {showNotification} = useNotification();

    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [currentFieldToUpdate, setCurrentFieldToUpdate] = useState(null);
    const [currentAspectRatio, setCurrentAspectRatio] = useState(IMAGE_REQUIREMENTS.vendor_photo.aspect_ratio);

    const [isVendorRequestFormOpen, setIsVendorRequestFormOpen] = useState(false);

    // --- СОСТОЯНИЕ ДЛЯ МОДАЛЬНОГО ОКНА КЛЕЙМА ---
    const [showClaimProfileModal, setShowClaimProfileModal] = useState(false);

    useEffect(() => {
        setVendorState(initialVendorProp);
        setLogo(initialVendorProp?.logo);
        setBackgroundImage(initialVendorProp?.background_image);
        setIsFavorite(initialVendorProp?.is_favorite || false);
    }, [initialVendorProp]);

    // Auto-open edit profile when instructed via origin
    useEffect(() => {
        if (visible && origin === 'edit-profile') {
            setShowEditProfilePopup(true);
        }
    }, [visible, origin]);

    const getRequestConfig = useCallback(() => {
        const token = localStorage.getItem('authToken');
        const csrfToken = Cookies.get('csrftoken');
        return {headers: {'Authorization': `Bearer ${token}`, 'X-CSRFToken': csrfToken,}, withCredentials: true,};
    }, []);

    const fetchVendorData = useCallback(async () => {
        if (!currentVendor?.vendor_id) {
            console.warn("fetchVendorData: currentVendor.vendor_id is not available for fetch.");
            return;
        }
        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.get(`/vendors/${currentVendor.vendor_id}/`, config);
            setVendorState(response.data);
            setLogo(response.data.logo);
            setBackgroundImage(response.data.background_image);
            setIsFavorite(response.data.is_favorite || false);
        } catch (error) {
            console.error('Ошибка загрузки данных вендора:', error);
            showNotification("Data Load Error", "Could not refresh vendor data. Please try again.", null, "error");
        } finally {
            setLoading(false);
        }
    }, [currentVendor?.vendor_id, showNotification, getRequestConfig]);

    const updateVendorData = async (vendorId, dataToUpdate) => {
        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/vendor/${vendorId}/update/`, dataToUpdate, config);
            return response;
        } catch (error) {
            console.error('Ошибка обновления вендора:', error);
            const errorData = error.response?.data;
            let errorMsg = "Failed to update vendor.";
            if (errorData) {
                if (typeof errorData === 'string') errorMsg = errorData;
                else if (errorData.detail) errorMsg = errorData.detail;
                else if (typeof errorData === 'object') {
                    const firstErrorKey = Object.keys(errorData)[0];
                    if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) errorMsg = `${firstErrorKey.replace(/_/g, ' ')}: ${errorData[firstErrorKey][0]}`;
                    else if (firstErrorKey) errorMsg = `${firstErrorKey.replace(/_/g, ' ')}: ${errorData[firstErrorKey]}`;
                } else {
                    errorMsg = "An unknown error occurred during update.";
                }
            }
            showNotification("Update Error", errorMsg, null, "error");
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleFieldUpdate = async (fieldName, value) => {
        if (!currentVendor?.vendor_id) return;
        const formData = new FormData();
        formData.append(fieldName, value);
        try {
            const response = await updateVendorData(currentVendor.vendor_id, formData);
            if (response.status === 200) {
                await fetchVendorData();
                showNotification("Update Success", `${fieldName.replace(/_/g, ' ')} updated successfully.`, null, "success");
            }
        } catch (error) {
            console.error(`Ошибка обновления поля ${fieldName}:`, error);
        }
    };

    const handleImageChangeCallback = useCallback((event, field, aspect) => {
        const file = event.target.files[0];
        if (file) {
            const tempURL = URL.createObjectURL(file);
            setImageToCrop(tempURL);
            setCurrentFieldToUpdate(field);
            setCurrentAspectRatio(aspect);
            setShowCropModal(true);
            event.target.value = null;
        }
    }, []);
    const handleLogoChange = useCallback((event) => handleImageChangeCallback(event, 'logo', IMAGE_REQUIREMENTS.vendor_photo.aspect_ratio), [handleImageChangeCallback]);
    const handleBackgroundImageChange = useCallback((event) => handleImageChangeCallback(event, 'background_image', IMAGE_REQUIREMENTS.background_vendor.aspect_ratio), [handleImageChangeCallback]);

    const handleCroppedImageComplete = async (croppedImageBlob) => {
        const field = currentFieldToUpdate;
        const originalImageSrc = imageToCrop;
        setShowCropModal(false);
        setImageToCrop(null);
        setCurrentFieldToUpdate(null);
        if (!croppedImageBlob || !field) {
            if (originalImageSrc) URL.revokeObjectURL(originalImageSrc);
            return;
        }
        try {
            const fileName = field === 'logo' ? 'logo_cropped.jpg' : 'background_cropped.jpg';
            const file = new File([croppedImageBlob], fileName, {type: croppedImageBlob.type || 'image/jpeg'});
            const requirements = (field === 'logo') ? IMAGE_REQUIREMENTS['vendor_photo'] : IMAGE_REQUIREMENTS['background_vendor'];
            const validatedAndScaledFile = await validateAndScaleImage(file, requirements.min_width, requirements.min_height, requirements.aspect_ratio, requirements.ratio_tolerance);
            await handleFieldUpdate(field, validatedAndScaledFile);
        } catch (error) {
            showNotification('Image Processing Error', error.message || 'Failed to process image.', null, 'error');
        } finally {
            if (originalImageSrc) URL.revokeObjectURL(originalImageSrc);
        }
    };

    const handlerShowEditProfilePopup = useCallback(() => setShowEditProfilePopup(true), []);
    const handlerCloseEditProfilePopup = useCallback(() => setShowEditProfilePopup(false), []);
    const handlerShowEditLanguagesPopup = useCallback(() => setShowEditLanguagesPopup(true), []);
    const handlerCloseEditLanguagesPopup = useCallback(() => setShowEditLanguagesPopup(false), []);
    const handlerShowEditServicesPopup = useCallback(() => setShowEditServicesPopup(true), []);
    const handlerCloseEditServicesPopup = useCallback(() => setShowEditServicesPopup(false), []);
    const handlerShowEditAwardsPopup = useCallback(() => setShowEditAwardsPopup(true), []);
    const handlerCloseEditAwardsPopup = useCallback(() => setShowEditAwardsPopup(false), []);
    const handlerShowEditWorkersPopup = useCallback(() => setShowEditWorkersPopup(true), []);
    const handlerCloseEditWorkersPopup = useCallback(() => setShowEditWorkersPopup(false), []);

    const handleSaveProfile = async (updatedData) => {
        if (!currentVendor?.vendor_id) return;
        try {
            const response = await updateVendorData(currentVendor.vendor_id, updatedData);
            if (response.status === 200) {
                await fetchVendorData();
                handlerCloseEditProfilePopup();
                showNotification("Profile Saved", "Profile details updated successfully.", null, "success");
            }
        } catch (error) {
            console.error('Ошибка сохранения профиля:', error);
        }
    };
    const handleSaveLanguages = async (updatedLanguagesData) => {
        if (!currentVendor?.vendor_id) return;
        setLoading(true);
        try {
            const languagesToSave = updatedLanguagesData.map(lang => ({
                language_id: lang.language_id,
                name: lang.name,
                proficiency_level: lang.proficiency_level
            }));
            const config = getRequestConfig();
            const response = await api.patch(`/vendor/${currentVendor.vendor_id}/languages/`, {languages: languagesToSave}, config);
            if (response.status === 200) {
                await fetchVendorData();
                handlerCloseEditLanguagesPopup();
                showNotification("Languages Saved", "Languages updated.", null, "success");
            }
        } catch (error) {
            console.error('Ошибка сохранения языков:', error);
            showNotification("Save Error", error.response?.data?.detail || "Could not save languages.", null, "error");
        } finally {
            setLoading(false);
        }
    };
    const handleSaveServices = async (services) => {
        // 👇 Вот эта проверка решает проблему
        if (!currentVendor?.vendor_id || !currentVendor?.service_type?.service_type_id) {
            console.error('Ошибка: ID вендора или ID типа услуги отсутствует. Сохранение отменено.');
            showNotification("Save Error", "Cannot save services: The vendor's main service type is not set.", null, "error");
            return;
        }

        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/vendor/${currentVendor.vendor_id}/service-subtypes/update/`, {
                services: services
            }, config);

            if (response.status === 200) {
                await fetchVendorData();
                handlerCloseEditServicesPopup();
                showNotification("Services Saved", "Services updated.", null, "success");
            }
        } catch (error) {
            console.error('Ошибка сохранения услуг:', error);
            showNotification("Save Error", error.response?.data?.detail || "Could not save services.", null, "error");
        } finally {
            setLoading(false);
        }
    };
    const handleSaveAwards = async (formDataToSend) => {
        if (!currentVendor?.vendor_id) return;
        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/vendor/${currentVendor.vendor_id}/awards/`, formDataToSend, config);
            if (response.status === 200) {
                await fetchVendorData();
                handlerCloseEditAwardsPopup();
                showNotification("Awards Saved", "Awards updated.", null, "success");
            }
        } catch (error) {
            console.error('Ошибка сохранения наград:', error);
            showNotification("Save Error", error.response?.data?.detail || "Could not save awards.", null, "error");
        } finally {
            setLoading(false);
        }
    };
    const handleSaveWorkers = async (workersDataOrFormData) => {
        if (!currentVendor?.vendor_id) return;
        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/vendor/${currentVendor.vendor_id}/workers/`, workersDataOrFormData, config);
            if (response.status === 200) {
                await fetchVendorData();
                handlerCloseEditWorkersPopup();
                showNotification("Workers Saved", "Team updated.", null, "success");
            }
        } catch (error) {
            console.error('Ошибка сохранения работников:', error);
            showNotification("Save Error", error.response?.data?.detail || "Could not save workers.", null, "error");
        } finally {
            setLoading(false);
        }
    };

    const isCurrentUserVendorOwner = useCallback(() => {
        return isAuthenticated && user && currentVendor && user.id === currentVendor.user;
    }, [isAuthenticated, user, currentVendor]);

    const handleShowClaimProfileModal = useCallback(() => {
        if (!isAuthenticated) {
            showNotification("Login Required", "Please log in to claim a profile.", null, "warning");
            return;
        }
        setShowClaimProfileModal(true);
    }, [isAuthenticated, showNotification]);

    const handleCloseClaimProfileModal = useCallback(() => {
        setShowClaimProfileModal(false);
    }, []);

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');
        if (cleaned.length === 10 && !cleaned.startsWith('1')) {
            cleaned = '1' + cleaned;
        }
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
            const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
            if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
        }
        if (cleaned.length > 0 && !cleaned.startsWith('+')) return `+${cleaned}`;
        return phoneNumber;
    };

    const formatTimeToAMPM = (time) => {
        if (!time || typeof time !== 'string') return '';
        const parts = time.split(':');
        if (parts.length < 2) return time;
        let hour = parseInt(parts[0], 10);
        const minute = parseInt(parts[1], 10);
        if (isNaN(hour) || isNaN(minute)) return time;
        const period = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return `${hour}:${minute < 10 ? '0' : ''}${minute} ${period}`;
    };

    const groupWorkingHours = useCallback((wh = []) => {
        if (!Array.isArray(wh) || wh.length === 0) return ["Working hours not specified."];
        const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const hoursMap = daysOfWeek.reduce((map, day) => {
            map[day] = 'Closed';
            return map;
        }, {});

        wh.forEach(item => {
            if (item && item.day_of_week && item.start_time && item.end_time) {
                hoursMap[item.day_of_week] = `${formatTimeToAMPM(item.start_time)} - ${formatTimeToAMPM(item.end_time)}`;
            }
        });
        const groupedHours = [];
        let currentRange = null;
        let currentDays = [];
        const finalizeGroup = () => {
            if (currentDays.length > 0 && currentRange) {
                groupedHours.push(`${currentDays.length === 1 ? currentDays[0] : `${currentDays[0]}-${currentDays[currentDays.length - 1]}`}: ${currentRange}`);
            }
        };
        daysOfWeek.forEach(day => {
            const hours = hoursMap[day];
            if (currentRange === hours && hours !== 'Closed' && currentRange !== null) {
                currentDays.push(day);
            } else {
                finalizeGroup();
                currentDays = [day];
                currentRange = hours;
            }
        });
        finalizeGroup();
        return groupedHours.length > 0 ? groupedHours : ["Working hours not specified."];
    }, [formatTimeToAMPM]);

    useEffect(() => {
        if (visible && currentVendor) {
            document.title = currentVendor.name || "Vendor Profile";
            const updateMetaTag = (property, content) => {
                let metaTag = document.querySelector(`meta[property='${property}']`);
                if (metaTag) {
                    metaTag.setAttribute('content', content || '');
                } else {
                    metaTag = document.createElement('meta');
                    metaTag.setAttribute('property', property);
                    metaTag.setAttribute('content', content || '');
                    document.head.appendChild(metaTag);
                }
            };
            updateMetaTag('og:title', currentVendor.name);
            updateMetaTag('og:description', currentVendor.description);
            updateMetaTag('og:image', currentLogo || currentVendor.logo);
            updateMetaTag('og:url', window.location.href);
            updateMetaTag('og:type', 'profile');
        }
    }, [visible, currentVendor, currentLogo]);

    const {
        name = "Vendor Name",
        description = "No description provided.",
        address,
        email = "N/A",
        phone_number = "N/A",
        additional_phones = [],
        web_site,
        social_media = [],
        working_hours = [],
        detailed_service_regions = [],
        awards = [],
        foundation_year = null,
        service_type,
        languages = [],
        workers = [],
        service_subtypes = [],
        user: vendorOwnerId,
        vendor_id,
    } = currentVendor || {};

    const formattedAddress = address ? `${address.street_address || ''}, ${address.city?.name || ''}, ${address.state?.name || ''}, ${address.country?.name || ''}, ${address.zip_code?.zip_code || ''}`.replace(/, $/, '').replace(/^, /, '') : "Address not available";
    const googleMapsUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}` : "#";
    const workingHoursFormatted = groupWorkingHours(working_hours);


    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (!isAuthenticated || !currentVendor?.vendor_id) {
                setIsFavorite(false);
                return;
            }
            try {
                const config = getRequestConfig();
                const response = await api.get(`/check-favorite-vendor/${currentVendor.vendor_id}/`, config);
                setIsFavorite(response.data.is_favorite);
            } catch (error) {
                console.error('Ошибка проверки избранного (вендор):', error);
            }
        };
        if (visible && currentVendor?.vendor_id) {
            checkFavoriteStatus();
        }
    }, [currentVendor?.vendor_id, isAuthenticated, visible, getRequestConfig]);

    const handleToggleFavorites = async () => {
        if (!isAuthenticated) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (!currentVendor?.vendor_id) return;
        const action = isFavorite ? 'remove-favorite-vendor' : 'add-favorite-vendor';
        const payload = {vendor_id: currentVendor.vendor_id};
        setLoading(true);
        try {
            const config = getRequestConfig();
            const response = await api.post(`/${action}/`, payload, config);
            if (response.status === 200 || response.status === 201 || response.status === 204) {
                const newFavoriteStatus = !isFavorite;
                setIsFavorite(newFavoriteStatus);
                showNotification(
                    newFavoriteStatus ? "Added to Favorites" : "Removed from Favorites",
                    `${name} has been ${newFavoriteStatus ? 'added to' : 'removed from'} your favorites.`,
                    null, "success"
                );
            }
        } catch (error) {
            console.error(`Ошибка избранного:`, error);
            showNotification("Error", `Could not update favorites. ${error.response?.data?.detail || ''}`, null, "error");
        } finally {
            setLoading(false);
        }
    };

    const groupedServiceRegions = (detailed_service_regions || []).reduce((acc, region) => {
        if (region && region.city?.name && region.state?.name && region.zip_code?.zip_code) {
            const stateAbbr = stateAbbreviations[region.state.name] || region.state.name;
            const cityState = `${region.city.name}, ${stateAbbr}`;
            if (!acc[cityState]) {
                acc[cityState] = [];
            }
            acc[cityState].push(region.zip_code.zip_code);
        }
        return acc;
    }, {});

    const toggleCityExpansion = (city) => setExpandedCities(prev => ({...prev, [city]: !prev[city]}));

    const handleOpenVendorRequestForm = useCallback(() => {
        setIsVendorRequestFormOpen(true);
    }, []);

    const handleCloseVendorRequestForm = useCallback(() => {
        setIsVendorRequestFormOpen(false);
    }, []);

    const handleVendorRequestSentAndFormClosed = useCallback((messageFromServer) => {
        console.log("Форма запроса к вендору отправлена и закрыта. Сообщение от сервера (если есть):", messageFromServer);
    }, [showNotification]);

    if (!visible || !currentVendor) {
        return null;
    }

    const isOwner = isCurrentUserVendorOwner();

    const shouldShowClaimButton =
        vendorOwnerId != null &&
        vendorOwnerId === LAW_LAW_USER_ID &&
        !isOwner;

    const showPhoneNumber = vendorOwnerId !== LAW_LAW_USER_ID;

    return (
        <div className={`popup ${visible ? 'visible' : ''}`} id="vendor-profile-popup">
            <Notification/>
            {loading && <Loader/>}
            <button className="close js--hide-popup" onClick={onClose}>
                <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
            </button>
            <div className="profile profile-pro vendor-profile">
                <VendorBanner
                    currentBackgroundImage={currentBackgroundImage}
                    currentLogo={currentLogo}
                    name={name}
                    isCurrentUserVendorOwner={isOwner}
                    handleLogoChange={handleLogoChange}
                    handleBackgroundImageChange={handleBackgroundImageChange}
                />

                <div className="profile__block">
                    <div className="container">
                        <VendorProfileHeader
                            name={name}
                            isFavorite={isFavorite}
                            handleToggleFavorites={handleToggleFavorites}
                            foundation_year={foundation_year}
                            service_type={service_type}
                            isCurrentUserVendorOwner={isOwner}
                            handlerShowEditProfilePopup={handlerShowEditProfilePopup}
                            isAuthenticated={isAuthenticated}
                        />
                        <VendorDetails
                            description={description}
                            address={address}
                            googleMapsUrl={googleMapsUrl}
                            formattedAddress={formattedAddress}
                            workingHoursFormatted={workingHoursFormatted}
                            email={email}
                            phone_number={phone_number}
                            formatPhoneNumber={formatPhoneNumber}
                            additional_phones={additional_phones}
                            web_site={web_site}
                            social_media={social_media}
                            showSharePopup={showSharePopup}
                            vendorName={name}
                            shareSpecializations={service_subtypes?.map(st => st.service_subtype?.name) || (service_type?.service_type_name ? [service_type.service_type_name] : []) || []}
                            isOwner={isOwner}
                            isAuthenticated={isAuthenticated}
                            onOpenRequestForm={handleOpenVendorRequestForm}
                            shouldShowClaimButton={shouldShowClaimButton}
                            onClaimProfileClick={handleShowClaimProfileModal}
                            vendorId={vendor_id}
                            showPhoneNumber={showPhoneNumber}
                        />
                    </div>
                </div>

                <VendorServicesSection
                    isCurrentUserVendorOwner={isOwner}
                    service_subtypes={service_subtypes}
                    handlerShowEditServicesPopup={handlerShowEditServicesPopup}
                />
                <VendorLanguagesSection
                    isCurrentUserVendorOwner={isOwner}
                    languages={languages}
                    handlerShowEditLanguagesPopup={handlerShowEditLanguagesPopup}
                />

                {/*{(isOwner || (detailed_service_regions && detailed_service_regions.length > 0)) && (*/}
                {/*    <div className="profile__block">*/}
                {/*        <div className="container">*/}
                {/*            <div className="profile__header">*/}
                {/*                <h2>Service Regions</h2>*/}
                {/*                {isOwner && (*/}
                {/*                    <button*/}
                {/*                        className="button no-style">*/}
                {/*                        <img src="/assets/icon--edit-Z9QfuMgm.svg" alt="Edit"/>*/}
                {/*                        {(detailed_service_regions?.length || 0) === 0 ? 'Add Regions' : 'Edit Regions'}*/}
                {/*                    </button>*/}
                {/*                )}*/}
                {/*            </div>*/}
                {/*            <div className="profile__content">*/}
                {/*                <ul className="params__list">*/}
                {/*                    {Object.keys(groupedServiceRegions).length > 0 ? (*/}
                {/*                        Object.keys(groupedServiceRegions).map((cityState, index) => (*/}
                {/*                            <li key={cityState || `region-${index}`}>*/}
                {/*                                <div onClick={() => toggleCityExpansion(cityState)}*/}
                {/*                                     style={{cursor: 'pointer', fontWeight: 'bold'}}>*/}
                {/*                                    {cityState}*/}
                {/*                                </div>*/}
                {/*                                {expandedCities[cityState] && typeof ZipCodes === 'function' &&*/}
                {/*                                    <ZipCodes zipCodes={groupedServiceRegions[cityState]}/>}*/}
                {/*                            </li>*/}
                {/*                        ))*/}
                {/*                    ) : (*/}
                {/*                        !isOwner && <li>No service regions specified.</li>*/}
                {/*                    )}*/}
                {/*                </ul>*/}
                {/*                {isOwner && Object.keys(groupedServiceRegions).length === 0 &&*/}
                {/*                    <p className="profile__no-data-message">Service regions not specified. Add them in*/}
                {/*                        your profile settings.</p>}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*)}*/}

                <VendorAwardsSection
                    isCurrentUserVendorOwner={isOwner}
                    awards={awards}
                    handlerShowEditAwardsPopup={handlerShowEditAwardsPopup}
                />
                <VendorWorkersSection
                    isCurrentUserVendorOwner={isOwner}
                    workers={workers}
                    handlerShowEditWorkersPopup={handlerShowEditWorkersPopup}
                />
            </div>
            {/* /.profile */}

            {isOwner && showEditProfilePopup && (
                <EditProfilePopup show={showEditProfilePopup} closeEditProfilePopup={handlerCloseEditProfilePopup}
                                  initialData={currentVendor} onSave={handleSaveProfile} isVendor={true}/>)}
            {isOwner && showEditLanguagesPopup && (<EditLanguagesPopup show={showEditLanguagesPopup}
                                                                       closeEditLanguagesPopup={handlerCloseEditLanguagesPopup}
                                                                       initialData={{languages: currentVendor?.languages || []}}
                                                                       onSave={handleSaveLanguages} entityType="vendor"
                                                                       entityId={currentVendor?.vendor_id}/>)}
            {isOwner && showEditServicesPopup && (
                <EditServicesPopup show={showEditServicesPopup} onClose={handlerCloseEditServicesPopup}
                                   initialSelectedSubtypes={currentVendor?.service_subtypes || []}
                                   vendorId={currentVendor?.vendor_id}
                                   onSave={handleSaveServices}
                                   serviceTypeId={currentVendor?.service_type?.service_type_id}
                                   onServicesUpdated={fetchVendorData}/>)}
            {isOwner && showEditAwardsPopup && (
                <EditAwardsPopup show={showEditAwardsPopup}
                                 onClose={handlerCloseEditAwardsPopup}
                                 initialData={{awards: currentVendor?.awards || []}}
                                 onSave={handleSaveAwards}
                                 entityId={currentVendor?.vendor_id} entityType="vendor"/>)}
            {isOwner && showEditWorkersPopup && (
                <EditWorkersPopup
                    show={showEditWorkersPopup}
                    onClose={handlerCloseEditWorkersPopup}
                    initialData={{workers: currentVendor?.workers || []}}
                    vendorId={currentVendor?.vendor_id}
                    onWorkersUpdated={fetchVendorData}
                    onSave={handleSaveWorkers}
                />
            )}
            {isOwner && showCropModal && imageToCrop && (
                <CropImageModal
                    show={showCropModal}
                    onClose={() => {
                        if (imageToCrop) URL.revokeObjectURL(imageToCrop);
                        setShowCropModal(false);
                        setImageToCrop(null);
                        setCurrentFieldToUpdate(null);
                    }}
                    image={imageToCrop}
                    aspect={currentAspectRatio}
                    onComplete={handleCroppedImageComplete}
                />
            )}

            {showClaimProfileModal && (
                <ClaimVendorProfileModal
                    show={showClaimProfileModal}
                    vendorToClaim={currentVendor}
                    onClose={handleCloseClaimProfileModal}
                />
            )}

            <PopupVendorRequestForm
                isOpen={isVendorRequestFormOpen}
                onClose={handleCloseVendorRequestForm}
                onSubmitSuccess={handleVendorRequestSentAndFormClosed}
                initialStateId={currentVendor?.address?.state?.state_id || currentVendor?.address?.state?.id}
                initialCityId={currentVendor?.address?.city?.city_id || currentVendor?.address?.city?.id}
                vendorId={currentVendor.vendor_id}
            />
        </div>
    );
};

export default PopupVendor;
